import * as React from "react";
import { FC, useCallback, useEffect } from "react";
import { useDataProvider, useNotify, useRefresh } from 'react-admin';
import { useHistory, useParams } from "react-router-dom";
import {
    Checkbox,
    createStyles,
    Divider,
    FormControlLabel,
    Grid,
    makeStyles,
    TextField,
    Theme,
    Chip,
    Tooltip
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { countries, server_companies, server_types } from "../../countries";
import { Button } from "@mui/material";
import HashToken from "./helpers/hashToken";
import NimbleToken from "./helpers/nimbleToken";
import AkamaiToken from "./helpers/akamaiToken";
import { HashTokenData } from "./helpers/types/hashTokenData";
import { NimbleTokenData } from "./helpers/types/nimbleTokenData";
import { AkamaiTokenData } from "./helpers/types/akamaiTokenData";

// import components
import Title from "../../components/Title";
import VerizonToken from "./helpers/verizonToken";
import LimelightToken from "./helpers/limelightToken";
import { VerizonTokenData } from "./helpers/types/verizonTokenData";
import { LimelightTokenData } from "./helpers/types/limelightTokenData";



const EditServer: FC = () => {

    const renderTokenConfig = (type: any) => {
        switch (type) {
            case 'hash':

                return (
                    <Grid item md={3}>
                        <HashToken hash_token={getHashToken} init_value={token_value} />
                    </Grid>
                )

            case 'nimble_token':

                return (<NimbleToken nimble_token={getNimbleToken} nimble_key={typeof token_value === 'undefined' ? "" : token_value.key} window={typeof token_value === 'undefined' ? "" : token_value.window} />)
            case 'akamai':
                return (<AkamaiToken akamai_token={getAkamaiToken} akamai_key={typeof token_value === 'undefined' ? "" : token_value.key} window={typeof token_value === 'undefined' ? "" : token_value.window} />)
            case 'verizon':
                return (<VerizonToken verizon_token={getVerizonToken} verizon_key={typeof token_value === 'undefined' ? "" : token_value.key} window={typeof token_value === 'undefined' ? "" : token_value.window} verizon_protocol={typeof token_value === 'undefined' ? "" : token_value.proto_allowed} />)
            case 'limelight':
                return (<LimelightToken limelight_token={getLimelightToken} limelight_key={typeof token_value === 'undefined' ? "" : token_value.secret} expire={typeof token_value === 'undefined' ? "" : token_value.expire} />)
        }
    }

    const useStyles = makeStyles((theme: Theme) =>
        createStyles({
            root: {
                padding: 30
            },
            large: {
                marginTop: 10,
                marginLeft: 10,
                marginBottom: 10,
                width: theme.spacing(10),
                height: theme.spacing(10),
            }
        })
    );
    const classes = useStyles();
    const dataProvider = useDataProvider();
    const { id } = useParams();
    const notify = useNotify();
    const history = useHistory();
    const refresh = useRefresh();

    const [server_country, setCountry] = React.useState<any>([]);
    const [server_address, setAddress] = React.useState<any>({});
    const [base_url, setUrl] = React.useState<any>("");
    const [connections_threshold, setConn] = React.useState<any>({});
    const [out_rate_threshold, setOut] = React.useState<any>({});
    const [connections_live, setConnLive] = React.useState<any>({});
    const [outrate_live, setOutLive] = React.useState<any>({});
    const [server_status, setStatus] = React.useState<any>({});
    const [server_status_info, setStatusInfo] = React.useState<any>({});
    const [server_status_full_info, setStatusFullInfo] = React.useState<any>({});
    const [rating, setRating] = React.useState<any>({});
    const [type, setType] = React.useState<any>("");
    const [credentials, setCredentials] = React.useState<any>("");
    const [serverDetails, setServerDetails] = React.useState<any>();
    const [updated_at, setUpdatedAt] = React.useState<any>();
    const [input_type, setInputType] = React.useState<any>('');
    const [encryption, setEncryption] = React.useState<any>(false);
    const [encryption_label, setEncryptionLabel] = React.useState<any>('');
    const [token_value, setTokenValue] = React.useState<any>('');
    const [token_type, setTokenType] = React.useState<any>('');
    const [app_name, setAppName] = React.useState<any>('');
    const [server_owner, setOwner] = React.useState<any>({});



    async function parseCountries(countries_response: []) {
        let result: { key: string, name: string }[] = [];
        for (let i = 0; i < countries_response.length; i++) {
            let obj = countries.find(o => o.key === countries_response[i]);
            if (obj) {
                result.push(obj);
            }
        }
        return result;
    }
    async function parseConfig(config_string: string) {
        try {
            return JSON.parse(config_string);
        }
        catch (e) {
            return config_string;
        }
    }

    function splitAppName(app_name: string) {
        try {
            return app_name.split(',');
        }
        catch (e) {
            return [app_name];
        }

    }

    const fetchServerDetails = useCallback(async () => {
        console.log(id);
        await dataProvider.getOne(
            'servers',
            {
                id: id
            },
            {
                onSuccess: async (response) => {
                    const parsed_countries = await parseCountries(response.data.server_country);
                    let parsed_config = response.data.token_config;
                    setTokenType(response.data.token);
                    switch (response.data.token) {
                        case 'hash':
                            setEncryption(response.data.token === "hash");
                            setEncryptionLabel("AES Encryption");
                            setTokenValue(parsed_config);
                            break;
                        case 'nimble_token':
                            parsed_config = await parseConfig(parsed_config);
                            setEncryption(response.data.token === "nimble_token");
                            setEncryptionLabel("Nimble Encryption");
                            setTokenValue(parsed_config);
                            break;
                        case 'akamai':
                            parsed_config = await parseConfig(parsed_config);
                            setEncryption(response.data.token === "akamai");
                            setEncryptionLabel("Akamai Encryption");
                            setTokenValue(parsed_config);
                            break;
                        case 'verizon':
                            parsed_config = await parseConfig(parsed_config);
                            setEncryption(response.data.token === "verizon");
                            setEncryptionLabel("Verizon Encryption");
                            setTokenValue(parsed_config);
                            break;
                        case 'limelight':
                            parsed_config = await parseConfig(parsed_config);
                            setEncryption(response.data.token === "limelight");
                            setEncryptionLabel("Limelight Encryption");
                            setTokenValue(parsed_config);
                            break;
                    }
                    setServerDetails(response.data);
                    setCountry(parsed_countries);
                    setAddress(response.data.server_address);
                    setUrl(response.data.base_url);
                    setType(response.data.server_type);
                    setOwner(response.data.server_owner);
                    setCredentials(response.data.credentials);
                    setAppName(response.data.app_name ? response.data.app_name : '');
                    setConn(response.data.connections_threshold);
                    setOut(response.data.out_rate_threshold / 1000000);
                    setConnLive(response.data.connections_live);
                    setOutLive(Math.round(response.data.outrate_live / 1000000));
                    setStatus(response.data.server_status);
                    setStatusInfo(response.data.server_status_info);
                    setStatusFullInfo(response.data.server_status_full_info);
                    setRating(response.data.rating);
                    setUpdatedAt(new Date(response.data.updated_at).toLocaleString());

                }
            }
        );
    }, []);

    const onSubmit = (e) => {

        let countries = server_country.map(function (item) {
            return item['key'];
        });

        let app_name_array = typeof app_name === 'string' ? splitAppName(app_name) : app_name;

        const updated = {
            server_country: countries,
            server_address: server_address,
            server_owner: server_owner,
            base_url: base_url,
            credentials: credentials,
            token: encryption ? token_type : 'none',
            token_config: token_value,
            app_name: app_name_array,
            server_status: server_status,
            server_status_info: server_status_info,
            server_status_full_info: server_status_full_info,
            server_type: type,
            connections_threshold: parseInt(connections_threshold),
            out_rate_threshold: parseInt(out_rate_threshold) * 1000000,
        }

        dataProvider.update(
            'servers/update',
            {
                id: id,
                data: updated,
                previousData: {
                    id: id,
                    server_owner: serverDetails.server_owner,
                    server_country: serverDetails.server_country,
                    server_address: serverDetails.server_address,
                    base_url: serverDetails.base_url,
                    connections_threshold: serverDetails.connections_threshold,
                    out_rate_threshold: serverDetails.out_rate_threshold,
                    connections_live: serverDetails.connections_live,
                    outrate_live: serverDetails.outrate_live,
                    server_status: serverDetails.server_status,
                    server_status_info: serverDetails.server_status_info,
                    server_status_full_info: serverDetails.server_status_full_info,
                    rating: serverDetails.rating
                }
            },
            {
                onSuccess: () => {
                    const timer: ReturnType<typeof setTimeout> = setTimeout(() => refresh(), 1000);
                    notify('Your Server was successfully updated', 'info');
                    history.push("/servers");
                },
                onFailure: () => {
                    notify('Error: Your Server was not successfully updated', 'warning');
                }
            },
        )
    }

    const getHashToken: HashTokenData = (data: any) => {
        setTokenValue(data);
    }
    const getNimbleToken: NimbleTokenData = (data: any) => {
        setTokenValue(data);
    }
    const getAkamaiToken: AkamaiTokenData = (data: any) => {
        setTokenValue(data);
    }
    const getVerizonToken: VerizonTokenData = (data: any) => {
        setTokenValue(data);
    }
    const getLimelightToken: LimelightTokenData = (data: any) => {
        setTokenValue(data);
    }

    useEffect(() => {
        fetchServerDetails();
    }, []);
    return (
        <div className={classes.root}>
            <Grid container spacing={3}>
                <Grid item md={8}>
                    <Title title="General Details" description='General Details' paddingBottom={10} paddingLeft={5} paddingTop={0} />
                    <Grid container spacing={3}>
                        <Grid item md={2}>
                            <Tooltip title="Company which owns the server" placement='top-end' aria-multiline>
                                <Autocomplete
                                    value={server_owner}
                                    onChange={(event: any, newValue: string | null) => {
                                        setOwner(newValue);
                                    }}
                                    inputValue={input_type}
                                    onInputChange={(event, newInputValue) => {
                                        setInputType(newInputValue);
                                    }}
                                    id="controllable-states-demo"
                                    options={server_companies}
                                    renderInput={(params) => <TextField {...params} label="Server Owner" variant='outlined' />}
                                />
                            </Tooltip>
                        </Grid>
                        <Grid item md={6}>
                            <Tooltip title="Countries where the server is going to serve" placement='top-end' aria-multiline>
                                <Autocomplete
                                    multiple
                                    id="tags-outlined"
                                    value={server_country}
                                    options={countries}
                                    getOptionLabel={(option) => option.name}
                                    filterSelectedOptions
                                    onChange={(event, value) => setCountry(value)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            label="Countries"
                                            placeholder="Countries"

                                        />
                                    )}
                                />
                            </Tooltip>
                        </Grid>
                        <Grid item md={2}>
                            {server_status ?
                                <Chip style={{ backgroundColor: "green", color: "white", marginTop: 10 }} label="Active" />
                                :
                                <Chip style={{ backgroundColor: "red", color: "white", marginTop: 10 }} label="Inactive" />
                            }
                        </Grid>
                    </Grid>
                    <Title title="Server Details" description='Server Details' paddingBottom={15} paddingLeft={5} paddingTop={15} />
                    <Grid container spacing={3}>
                        <Grid item md={6}>
                            <Tooltip title="Hold the address where the server is going to be monitored Port depends on the provider (Nimble , Flussonic etc)" placement='top-end' aria-multiline>
                                <TextField
                                    label="Server Address"
                                    placeholder="Server Address"
                                    value={server_address}
                                    variant="outlined"
                                    fullWidth
                                    onChange={e => { setAddress(e.target.value) }}
                                />
                            </Tooltip>
                        </Grid>
                        <Grid item md={6}>
                            <Tooltip title="Holds the address where the server is going to serve streams Part of the Stream URL" placement='top-end' aria-multiline>
                                <TextField
                                    label='Base Url'
                                    placeholder="Base Url"
                                    value={base_url}
                                    variant="outlined"
                                    fullWidth
                                    onChange={e => { setUrl(e.target.value) }}
                                />
                            </Tooltip>
                        </Grid>
                    </Grid>
                    <Title title="Server Type & Live Data" description='Server Type & Live Data' paddingBottom={15} paddingLeft={5} paddingTop={15} />
                    <Grid container spacing={3}>
                        <Grid item md={2}>
                            <Tooltip title="Type of the server that is going to serve streams" placement='top-end' aria-multiline>
                                <Autocomplete
                                    value={type}
                                    onChange={(event: any, newValue: string | null) => {
                                        setType(newValue);
                                    }}
                                    inputValue={input_type}
                                    onInputChange={(event, newInputValue) => {
                                        setInputType(newInputValue);
                                    }}
                                    id="controllable-states-demo"
                                    options={server_types}
                                    renderInput={(params) => <TextField {...params} label="Server Type" variant='outlined' />}
                                />
                            </Tooltip>
                        </Grid>
                        <Grid item md={4}>
                            <Tooltip title="Credentials to use when the server protections is on (Apikey or other credentials depending on the server)" placement='top-end' aria-multiline>
                                <TextField
                                    label='Credentials'
                                    placeholder="Credentials"
                                    variant="outlined"
                                    value={credentials ? credentials : ""}
                                    fullWidth
                                    onChange={e => { setCredentials(e.target.value) }}
                                />
                            </Tooltip>
                        </Grid>
                    </Grid>
                    <Grid container spacing={3}>
                        <Grid item md={4}>
                            <Title title="Server application name" description='The application name that is configured in stream servers platform' paddingBottom={15} paddingLeft={5} paddingTop={15} />
                            <Tooltip title="The application name that is configured in stream servers platform" placement='top-end' aria-multiline>
                                <TextField
                                    label='Application Name'
                                    placeholder="Application Name"
                                    variant="outlined"
                                    value={app_name ? app_name : ""}
                                    fullWidth
                                    onChange={e => { setAppName(e.target.value) }}
                                />
                            </Tooltip>
                        </Grid>
                    </Grid>
                    <div style={{ padding: 10 }} />
                    <Grid container spacing={3}>
                        <Grid item md={12}>
                            <Title title="Encryption" description='Type of encryption used for the streams provided by this server' paddingBottom={15} paddingLeft={5} paddingTop={15} />
                            {token_type !== 'none' ? <Grid item md={2}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={encryption}
                                            onChange={e => {
                                                setEncryption(e.target.checked)
                                            }}
                                            color="primary"
                                        />
                                    }
                                    label={encryption_label}
                                />
                            </Grid> : ''}
                            {encryption ? renderTokenConfig(token_type) : ''}
                        </Grid>
                    </Grid>
                    <Title title="Server Capacity" description='Server Capacity' paddingBottom={15} paddingLeft={5} paddingTop={15} />
                    <Grid container spacing={3}>
                        <Grid item md={3}>
                            <Tooltip title="Maximum number of connections that the server is going to handle" placement='top-end' aria-multiline>
                                <TextField
                                    label='Connections Threshhold'
                                    placeholder="Connections Threshhold"
                                    value={connections_threshold ? connections_threshold : ''}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    onChange={e => { setConn(e.target.value) }}
                                />
                            </Tooltip>
                        </Grid>
                        <Grid item md={3}>
                            <Tooltip title="Maximum bandwidth that the server can handle , unit is in Mbps" placement='top-end' aria-multiline>
                                <TextField
                                    label='OutRate Threshhold(Mbps)'
                                    placeholder="OutRate Threshhold"
                                    value={out_rate_threshold ? out_rate_threshold : ''}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    onChange={e => { setOut(e.target.value) }}
                                />
                            </Tooltip>
                        </Grid>
                    </Grid>
                    <div style={{ padding: 10 }} />
                    <Grid container spacing={3}>
                        <Grid item md={3}>
                            <Tooltip title="Number of live connections" placement='top-end' aria-multiline>
                                <TextField
                                    disabled={true}
                                    label="Connections Live"
                                    placeholder="Connections Live"
                                    value={connections_live ? connections_live : ''}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                />
                            </Tooltip>
                        </Grid>
                        <Grid item md={3}>
                            <Tooltip title="Current bandwidth of the server" placement='top-end' aria-multiline>
                                <TextField
                                    disabled={true}
                                    label='OutRate Live'
                                    placeholder="OutRate Live"
                                    value={outrate_live ? outrate_live : ''}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                />
                            </Tooltip>
                        </Grid>
                    </Grid>
                    <div style={{ padding: 10 }} />
                    <Grid container spacing={3}>
                        <Grid item md={3}>
                            <Tooltip title="Current load of the server" placement='top-end' aria-multiline>
                                <TextField
                                    disabled={true}
                                    label='Rating'
                                    placeholder="Rating"
                                    value={rating ? rating : ''}
                                    variant="outlined"
                                    size='small'
                                    fullWidth
                                />
                            </Tooltip>
                        </Grid>
                    </Grid>
                    <Title title="Sample URL" description='Sample URL' paddingBottom={15} paddingLeft={5} paddingTop={15} />
                    <Grid container spacing={3}>
                        <Grid item md={6}>
                            <TextField
                                disabled={true}
                                label='Sample url'
                                placeholder="Rating"
                                value={`http://${typeof base_url != 'object' ? base_url : ""}/yWbXvr1S/testarbiter/playlist.m3u8`}
                                variant="outlined"
                                size="small"
                                fullWidth
                            />
                        </Grid>
                        <Grid item md={5}>
                            <TextField
                                disabled={true}
                                label='Updated At'
                                placeholder="Updated At"
                                value={updated_at ? updated_at : ''}
                                variant="outlined"
                                size='small'
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                    <Title title="Server Status" description='The Status Server Full Info' paddingBottom={15} paddingLeft={5} paddingTop={15} />
                    <Grid container spacing={4}>
                        <Grid item md={6}>
                            <Tooltip title="Status" placement='top-end' aria-multiline>
                                <TextField
                                    disabled
                                    id="server_status_info"
                                    label="Status Info"
                                    placeholder="Status Info"
                                    variant="outlined"
                                    value={server_status_info}
                                    fullWidth
                                    onChange={e => setStatusInfo(e.target.value)}
                                    multiline
                                    rows={4}
                                />
                            </Tooltip>
                        </Grid>
                        <Grid item md={5}>
                            <Tooltip title="Status" placement='top-end' aria-multiline>
                                {/* <Chip label="server status full info"  /> */}
                                <TextField
                                    disabled
                                    id="server_status_full_info"
                                    label="Status Full Info"
                                    placeholder="Status Full Info"
                                    variant="outlined"
                                    value={server_status_full_info}
                                    fullWidth
                                    onChange={e => setStatusInfo(e.target.value)}
                                    multiline
                                    rows={4}
                                />
                            </Tooltip>
                        </Grid>
                    </Grid>
                    <div style={{ padding: 20 }}>
                        <Divider />
                    </div>
                    <Button
                        size="large"
                        color="primary"
                        variant="contained"
                        type="button"
                        onClick={onSubmit}
                    >
                        Submit
                    </Button>
                </Grid>
            </Grid>
        </div>
    )

}

export default EditServer;